import { formatNumber, Sparkline, StatCard, type StatDelta, useTranslation } from '@hbs-us/ui';

import type { OverviewStats } from '../overviewTypes.js';

type IntentMode = 'good-up' | 'good-down';

function deltaFor(
  current: number,
  previous: number | undefined,
  locale: string,
  mode: IntentMode,
): StatDelta | undefined {
  if (previous == null || previous === 0) return undefined;

  const movement = current === previous ? 'flat' : current > previous ? 'up' : 'down';
  const intent =
    movement === 'flat'
      ? 'neutral'
      : (movement === 'up') === (mode === 'good-up')
        ? 'positive'
        : 'negative';

  return {
    direction: movement,
    intent,
    value: formatNumber((current - previous) / previous, locale, {
      style: 'percent',
      signDisplay: 'always',
      maximumFractionDigits: 1,
    }),
  };
}

export function KpiTiles({
  stats,
  money,
}: {
  stats: OverviewStats;
  money: (value: number) => string;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const revenueSeries = stats.monthlySeries.map((point) => point.revenue);
  const collectedSeries = stats.monthlySeries.map((point) => point.collected);

  return (
    <section aria-label={t('home:kpis.heading')} className="grid gap-3">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label={t('home:kpis.revenue')}
          value={money(stats.revenueMtd)}
          delta={deltaFor(stats.revenueMtd, stats.prevRevenueMtd, locale, 'good-up')}
          footnote={
            stats.prevRevenueMtd == null
              ? undefined
              : t('home:kpis.vsLastMonth', { value: money(stats.prevRevenueMtd) })
          }
          chart={<Sparkline data={revenueSeries} tone="onPrimary" />}
        />
        <StatCard
          label={t('home:kpis.openJobs')}
          value={formatNumber(stats.activeJobs, locale)}
          delta={{ direction: 'flat', intent: 'neutral', value: t('home:kpis.live') }}
          footnote={t('home:kpis.onHold', { count: formatNumber(stats.onHoldJobs, locale) })}
        />
        <StatCard
          label={t('home:kpis.collected')}
          value={money(stats.collectedMtd)}
          delta={deltaFor(stats.collectedMtd, stats.prevCollectedMtd, locale, 'good-up')}
          footnote={t('home:kpis.ofInvoiced', { value: money(stats.invoicedMtd) })}
          chart={<Sparkline data={collectedSeries} tone="success" />}
        />
        <StatCard
          label={t('home:kpis.overdueAr')}
          value={money(stats.overdueInvoiceTotal)}
          delta={deltaFor(
            stats.overdueInvoiceTotal,
            stats.prevOverdueInvoiceTotal,
            locale,
            'good-down',
          )}
          footnote={t('home:kpis.invoiceCount', {
            count: stats.overdueInvoiceCount,
            value: formatNumber(stats.overdueInvoiceCount, locale),
          })}
        />
      </div>
    </section>
  );
}
